import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';

import {
  CreateDocumentUploadType,
  UpdateDocumentUploadType,
  ListDocumentUploadType,
  DocumentUploadAuditType,
  GetDocumentUploadParamsType

} from '../types/documentuploadtype';

import { ENV } from "@app/constants/env";
import { getToken } from '@app/helpers/authProvider';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: ENV.BASE_API_URL +ENV.DMS_APIURL,
});
const authBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  incomingArgs,
  api,
  extraOptions
) => {
  let args: string | FetchArgs = incomingArgs;

  // normalize string arg to object so we can set headers
  if (typeof args === 'string') {
    args = { url: args };
  }

  try {
    const token = await getToken(); // ensure this returns a non-empty string
    if (token) {
      const headers =
        (args as FetchArgs).headers instanceof Headers
          ? (args as FetchArgs).headers
          : new Headers((args as FetchArgs).headers);

      headers.set('Authorization', `Bearer ${token}`); // use standard casing
      (args as FetchArgs).headers = headers;
    }

    const result = await rawBaseQuery(args, api, extraOptions);

    // optional: retry once if unauthorized (adjust getToken refresh logic if available)
    if (result.error && result.error.status === 401 && token) {
      // If you have a refresh flow, invoke it here then retry.
      // const refreshed = await getRefreshedToken();
      // if (refreshed) { headers.set('Authorization', `Bearer ${refreshed}`); return rawBaseQuery(args, api, extraOptions); }
    }

    return result;
  } catch {
    return { error: { status: 500, data: 'Token retrieval failed' } } as any;
  }
};
export interface ApiResponse<T> {
  success: boolean
  message?: string
  data?: T
  error?: string
}
export const commonDMSApi = createApi({
  reducerPath: 'commonDMSApi',
  baseQuery: authBaseQuery,
  tagTypes: ['commonDMS'],
  endpoints: (builder) => ({


    getDocumentUpload: builder.query<ApiResponse<ListDocumentUploadType[]>, GetDocumentUploadParamsType>({
      
      query: (params) => ({
        url: '/DocumentsUpload/DocumentUploadRead',
        method: 'POST',

        headers: {
          'Content-Type': 'application/json',
        },
        body: params,

      }),
      providesTags: ['commonDMS'],
    }),
    // createDocumentUpload: builder.mutation<ApiResponse<void>, CreateDocumentUploadType>({
    //   query: (body) => ({
    //     url: 'DocumentsUpload/DocumentUploadCreate',
    //     method: 'POST',
    //     body,
    //       headers: {
    //         'Content-Type': 'multipart/form-data',
    //       },
    //   }),
    //   invalidatesTags: ['commonDMS'],
    // }),

    // createDocumentUpload: builder.mutation<ApiResponse<void>, any>({
    //   query: (payload) => ({
    //     url: 'DocumentsUpload/DocumentUploadCreate',
    //     method: 'POST',
    //     body: { createdto: payload.createdto || payload },
    //     headers: {
    //       'Content-Type': 'application/json',
    //     },
    //   }),
    //   invalidatesTags: ['commonDMS'],
    // }),

    createDocumentUpload: builder.mutation<ApiResponse<void>, any>({
      query: (payload) => ({
        url: '/DocumentsUpload/DocumentUploadCreate',
        method: 'POST',
        body: payload,   // ✅ REMOVE wrapper
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: ['commonDMS'],
    }),
    // createDocumentUpload: builder.mutation<ApiResponse<void>, CreateDocumentUploadType>({
    //   query: (body) => {
    //     const formData = new FormData();
    //     // Assuming body is an object with keys and values (including files)
    //     Object.entries(body).forEach(([key, value]) => {
    //       if (value !== undefined && value !== null) {
    //         formData.append(key, value as any);
    //       }
    //     });
    //     return {
    //       url: 'DocumentsUpload/DocumentUploadCreate',
    //       method: 'POST',
    //       body: formData,
    //       // Do NOT set Content-Type here!
    //     };
    //   },
    //   invalidatesTags: ['commonDMS'],
    // }),
    updateDocumentUpload: builder.mutation<ApiResponse<void>, any>({
      query: (payload) => ({
        url: '/DocumentsUpload/DocumentUploadUpdate',
        method: 'PUT',
        body: payload,   // ✅ REMOVE wrapper
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: ['commonDMS'],
    }),
    deleteDocumentUpload: builder.mutation<ApiResponse<void>, number>({
      query: (documentuploadid) => ({
        url: `/DocumentsUpload/DocumentUploadDestroy/${documentuploadid}`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: ['commonDMS'],
    }),
    getDocumentUploadAudit: builder.query<ApiResponse<DocumentUploadAuditType>, number>({
      query: (documentuploadid) => ({
        url: `/DocumentsUpload/DocumentUploadAudit/${documentuploadid}`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      providesTags: ['commonDMS'],
    }),
    getDocumentDownload: builder.query({
      query: (document) => {
        // Replace with your actual username and password
        const username = 'your_username';
        const password = 'your_password';
        const basicAuth = btoa(`${username}:${password}`);

        return {
          url: `${ENV.DMS_DOWNLOAD_APIURL}/${document.DMSObjectID}/content-media?format=${document.Ext}&modifier=&page=0`,
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Basic ${basicAuth}`,
            // If you need to send username and password as custom headers (not standard for Basic Auth):
            // 'DMS_Username': username,
            // 'DMS_Password': password,
          },
        };
      },
      providesTags: ['commonDMS'],
    }),

    // Create signature document - specialized endpoint for signature creation
    createDocumentUploadSign: builder.mutation<ApiResponse<{ documentUploadId: number }>, any>({
      query: (payload) => ({
        url: '/DocumentsUpload/DocumentUploadCreateSign',
        method: 'POST',
        body: payload,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: ['commonDMS'],
    }),

    // Update signature document - specialized endpoint for signature update
    updateDocumentUploadSign: builder.mutation<ApiResponse<void>, any>({
      query: (payload) => ({
        url: '/DocumentsUpload/DocumentUploadUpdateSign',
        method: 'PUT',
        body: payload,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: ['commonDMS'],
    }),
        getDocumentUploadReadDamagePhoto: builder.query<ApiResponse<ListDocumentUploadType[]>, any>({
            query: (params) => ({
              url: '/DocumentsUpload/DocumentUploadReadDamagePhoto',
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: params,
            }),
            providesTags: ['commonDMS'],
          }),
        
        getDocumentUploadReadSign: builder.query<ApiResponse<ListDocumentUploadType[]>, any>({
            query: (params) => ({
              url: '/DocumentsUpload/DocumentUploadReadSign',
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: params,
            }),
            providesTags: ['commonDMS'],
          }),
        
        // Proxy endpoint to fetch DMS image content (avoids CORS issues)
        getDMSImageProxy: builder.query<Blob, { dmsObjectID: string; fileExt: string; dmsUrl: string }>({
            query: ({ dmsObjectID, fileExt, dmsUrl }) => ({
              url: '/DocumentsUpload/GetDMSImageProxy',
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: { dmsObjectID, fileExt, dmsUrl },
              responseHandler: async (response) => {
                return await response.blob();
              },
            }),
            providesTags: ['commonDMS'],
          }),

        // Get document upload by ID - returns document with blob URL
        getDocumentUploadById: builder.query<any, string>({
          query: (documentUploadId) => ({
            url: `/DocumentsUpload/GetDocumentUploadById/${documentUploadId}`,
            method: 'GET',
          }),
          providesTags: ['commonDMS'],
        }),

  }),
});
export const {
  useGetDocumentUploadQuery,
  useGetDocumentUploadReadDamagePhotoQuery,
  useGetDocumentUploadReadSignQuery,
  useGetDMSImageProxyQuery,
  useLazyGetDMSImageProxyQuery,
  useCreateDocumentUploadMutation,
  useUpdateDocumentUploadMutation,
  useDeleteDocumentUploadMutation,
  useGetDocumentUploadAuditQuery,
  useGetDocumentDownloadQuery,
  useCreateDocumentUploadSignMutation,
  useUpdateDocumentUploadSignMutation,
  useGetDocumentUploadByIdQuery,
  useLazyGetDocumentUploadByIdQuery,
} = commonDMSApi;